/* eslint-disable max-lines */
import React, {
 useState, useEffect, useMemo
} from 'react';
import {
 useParams
} from 'react-router-dom';
import {
 Card, CardContent
} from 'shared/components/ui/card';
import {
 Button
} from 'shared/components/ui/button';
import {
 Input
} from 'shared/components/ui/input';
import {
 Label
} from 'shared/components/ui/label';
import {
 Switch
} from 'shared/components/ui/switch';
import {
 Badge
} from 'shared/components/ui/badge';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from 'shared/components/ui/select';
import {
 ArrowLeft, Save, Database, Clock, Plus, Trash2
} from 'lucide-react';
import {
 schemaService
} from 'shared/api/services/BEXO';
import {
    TriggerListItem, TriggerEventType, DataChangeEvent, ScheduleFrequency,
    DataChangeConfig, ScheduleConfig, TriggerCondition, TriggerAction,
} from '../types/trigger.types';
import ConditionBuilder from './trigger-editor/ConditionBuilder';

interface TriggerEditorProps {
    trigger: TriggerListItem | null;
    onSave: (data: Omit<TriggerListItem, 'id' | 'createdAt' | 'updatedAt' | 'lastFired'>) => void;
    onCancel: () => void;
}

const dataChangeEvents: { value: DataChangeEvent; label: string }[] = [
    {
 value: 'CREATE',
label: 'On CREATE'
},
    {
 value: 'UPDATE',
label: 'On UPDATE'
},
    {
 value: 'DELETE',
label: 'On DELETE'
},
];

const scheduleFrequencies: { value: ScheduleFrequency; label: string }[] = [
    {
 value: 'every_minute',
label: 'Every minute'
},
    {
 value: 'every_hour',
label: 'Every hour'
},
    {
 value: 'every_day',
label: 'Every day'
},
    {
 value: 'custom',
label: 'Custom (Cron)'
},
];

function parseCronToHuman(cron: string): string {
    if (!cron.trim()) return '';
    const parts = cron.trim().split(/\s+/);
    if (parts.length !== 5) return 'Invalid cron expression';

    const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

    if (cron === '* * * * *') return 'Runs every minute';
    if (cron === '0 * * * *') return 'Runs every hour';
    if (cron === '0 0 * * *') return 'Runs every day at midnight';
    if (cron === '0 0 * * 0') return 'Runs every Sunday at midnight';
    if (cron === '0 0 1 * *') return 'Runs on the 1st of every month';

    // */N patterns
    if (minute.startsWith('*/') && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
        const n = minute.slice(2);
        return `Runs every ${n} minute${n === '1' ? '' : 's'}`;
    }
    if (minute === '0' && hour.startsWith('*/') && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
        const n = hour.slice(2);
        return `Runs every ${n} hour${n === '1' ? '' : 's'}`;
    }

    return `Cron: ${cron}`;
}

const TriggerEditor: React.FC<TriggerEditorProps> = ({
 trigger, onSave, onCancel
}) => {
    const {
 appId, oragID
} = useParams<{ appId: string; oragID: string }>();

    // Form state
    const [name, setName] = useState(trigger?.name || '');
    const [active, setActive] = useState(trigger?.active ?? true);
    const [eventType, setEventType] = useState<TriggerEventType>(trigger?.eventType || 'data_change');

    // Data Change config
    const [schemaName, setSchemaName] = useState<string>(
        trigger?.eventType === 'data_change' ? (trigger.eventConfig as DataChangeConfig).schemaName : ''
    );
    const [dataChangeEvent, setDataChangeEvent] = useState<DataChangeEvent>(
        trigger?.eventType === 'data_change' ? (trigger.eventConfig as DataChangeConfig).event : 'UPDATE'
    );

    // Schedule config
    const [frequency, setFrequency] = useState<ScheduleFrequency>(
        trigger?.eventType === 'schedule' ? (trigger.eventConfig as ScheduleConfig).frequency : 'every_hour'
    );
    const [cronExpression, setCronExpression] = useState<string>(
        trigger?.eventType === 'schedule' ? (trigger.eventConfig as ScheduleConfig).cronExpression : ''
    );

    // Conditions
    const [conditions, setConditions] = useState<TriggerCondition[]>(trigger?.conditions || []);

    // Action
    const [workflowName, setWorkflowName] = useState(trigger?.action.workflowName || '');
    const [inputMappings, setInputMappings] = useState<{ inputName: string; value: string }[]>(
        trigger?.action.inputMappings || []
    );

    // Schema data
    const [schemaNames, setSchemaNames] = useState<string[]>([]);
    const [schemaColumns, setSchemaColumns] = useState<string[]>([]);
    const [isLoadingSchemas, setIsLoadingSchemas] = useState(true);

    // Load schema names
    useEffect(() => {
        const loadSchemas = async () => {
            if (!appId || !oragID) return;
            try {
                const res = await schemaService.retrieveSchemas('*', appId, oragID);
                const rawSchemas = res.data?.schemas || res.data || [];
                if (Array.isArray(rawSchemas)) {
                    const names = new Set<string>();
                    rawSchemas.forEach((item: any) => {
                        const n = item.tableName || item.table_name;
                        if (n) names.add(n);
                    });
                    setSchemaNames(Array.from(names));
                }
            } catch (error) {
                console.error('Error loading schemas:', error);
            } finally {
                setIsLoadingSchemas(false);
            }
        };
        loadSchemas();
    }, [appId, oragID]);

    // Load columns when schema changes
    useEffect(() => {
        const loadColumns = async () => {
            if (!schemaName || !appId || !oragID) {
                setSchemaColumns([]);
                return;
            }
            try {
                const res = await schemaService.retrieveSchemas(schemaName, appId, oragID);
                const rawSchemas = res.data?.schemas || res.data || [];
                const columns: string[] = [];
                if (Array.isArray(rawSchemas)) {
                    rawSchemas.forEach((item: any) => {
                        if (Array.isArray(item.columns)) {
                            item.columns.forEach((col: any) => {
                                if (col.columnName) columns.push(col.columnName);
                            });
                        } else if (item.columnName) {
                            columns.push(item.columnName);
                        }
                    });
                }
                setSchemaColumns(columns);
            } catch (error) {
                console.error('Error loading columns:', error);
            }
        };
        loadColumns();
    }, [schemaName, appId, oragID]);

    const cronHumanReadable = useMemo(() => {
        if (frequency !== 'custom' || !cronExpression) return '';
        return parseCronToHuman(cronExpression);
    }, [frequency, cronExpression]);

    const addInputMapping = () => {
        setInputMappings((prev) => [...prev, {
 inputName: '',
value: ''
}]);
    };

    const updateInputMapping = (index: number, field: 'inputName' | 'value', val: string) => {
        setInputMappings((prev) => prev.map((m, i) => (i === index ? {
 ...m,
[field]: val
} : m)));
    };

    const removeInputMapping = (index: number) => {
        setInputMappings((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSave = () => {
        if (!name.trim()) return;

        const eventConfig = eventType === 'data_change'
            ? {
 schemaName,
event: dataChangeEvent
} as DataChangeConfig
            : {
 frequency,
cronExpression
} as ScheduleConfig;

        const action: TriggerAction = {
            type: 'execute_workflow',
            workflowId: '',
            workflowName,
            inputMappings,
        };

        onSave({
            name,
            active,
            eventType,
            eventConfig,
            conditions,
            action,
        });
    };

    // Available trigger data placeholders
    const triggerPlaceholders = useMemo(() => {
        if (eventType !== 'data_change' || schemaColumns.length === 0) return [];
        const placeholders: string[] = [];
        if (dataChangeEvent === 'UPDATE' || dataChangeEvent === 'CREATE') {
            schemaColumns.forEach((col) => placeholders.push(`{{trigger.record.after.${col}}}`));
        }
        if (dataChangeEvent === 'UPDATE' || dataChangeEvent === 'DELETE') {
            schemaColumns.forEach((col) => placeholders.push(`{{trigger.record.before.${col}}}`));
        }
        return placeholders;
    }, [eventType, dataChangeEvent, schemaColumns]);

    return (
        <div className="flex flex-col gap-6 w-full py-4 max-w-3xl">
            {/* Header */}
            <div>
                <Button variant="ghost" size="sm" onClick={onCancel} className="mb-4">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Triggers
                </Button>
                <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 space-y-1.5">
                        <h2 className="text-2xl font-bold">
                            {trigger ? 'Edit Trigger' : 'Create New Trigger'}
                        </h2>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g., On Order Paid, Start Fulfillment"
                            className="max-w-md"
                        />
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                        <div className="flex items-center gap-2">
                            <Label className="text-sm">Active</Label>
                            <Switch checked={active} onCheckedChange={setActive} />
                        </div>
                        <Button onClick={handleSave} disabled={!name.trim()}>
                            <Save className="mr-2 h-4 w-4" />
                            Save Trigger
                        </Button>
                    </div>
                </div>
            </div>

            {/* ── WHEN ── */}
            <div className="space-y-4">
                <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs font-bold px-3 py-1">WHEN</Badge>
                    <span className="text-sm text-muted-foreground">Choose what event starts this trigger</span>
                </div>

                {/* Event type cards */}
                <div className="grid grid-cols-2 gap-4">
                    <Card
                        className={`cursor-pointer transition-all ${
                            eventType === 'data_change'
                                ? 'ring-2 ring-primary shadow-sm'
                                : 'hover:shadow-sm hover:ring-1 hover:ring-primary/20'
                        }`}
                        onClick={() => setEventType('data_change')}
                    >
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className={`rounded-full p-2 ${eventType === 'data_change' ? 'bg-primary/10' : 'bg-muted'}`}>
                                <Database className="h-5 w-5" />
                            </div>
                            <div>
                                <h4 className="font-medium text-sm">Data Change</h4>
                                <p className="text-xs text-muted-foreground">React to database events</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card
                        className={`cursor-pointer transition-all ${
                            eventType === 'schedule'
                                ? 'ring-2 ring-primary shadow-sm'
                                : 'hover:shadow-sm hover:ring-1 hover:ring-primary/20'
                        }`}
                        onClick={() => setEventType('schedule')}
                    >
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className={`rounded-full p-2 ${eventType === 'schedule' ? 'bg-primary/10' : 'bg-muted'}`}>
                                <Clock className="h-5 w-5" />
                            </div>
                            <div>
                                <h4 className="font-medium text-sm">Schedule</h4>
                                <p className="text-xs text-muted-foreground">Run on a timer</p>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Data Change config */}
                {eventType === 'data_change' && (
                    <div className="grid grid-cols-2 gap-4 pl-4 border-l-2 border-primary/20">
                        <div className="space-y-1.5">
                            <Label className="text-sm">Schema</Label>
                            <Select
                                value={schemaName}
                                onValueChange={setSchemaName}
                                disabled={isLoadingSchemas}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder={isLoadingSchemas ? 'Loading...' : 'Select a schema'} />
                                </SelectTrigger>
                                <SelectContent>
                                    {schemaNames.map((n) => (
                                        <SelectItem key={n} value={n}>{n}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-sm">Event</Label>
                            <Select
                                value={dataChangeEvent}
                                onValueChange={(v) => setDataChangeEvent(v as DataChangeEvent)}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {dataChangeEvents.map((e) => (
                                        <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                )}

                {/* Schedule config */}
                {eventType === 'schedule' && (
                    <div className="space-y-4 pl-4 border-l-2 border-primary/20">
                        <div className="space-y-1.5">
                            <Label className="text-sm">Frequency</Label>
                            <Select
                                value={frequency}
                                onValueChange={(v) => setFrequency(v as ScheduleFrequency)}
                            >
                                <SelectTrigger className="max-w-xs">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {scheduleFrequencies.map((f) => (
                                        <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {frequency === 'custom' && (
                            <div className="space-y-1.5">
                                <Label className="text-sm">Cron Expression</Label>
                                <Input
                                    value={cronExpression}
                                    onChange={(e) => setCronExpression(e.target.value)}
                                    placeholder="*/5 * * * *"
                                    className="font-mono max-w-xs"
                                />
                                {cronHumanReadable && (
                                    <p className="text-xs text-muted-foreground">
                                        {cronHumanReadable}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ── IF ── */}
            <div className="space-y-4">
                <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs font-bold px-3 py-1">IF</Badge>
                    <span className="text-sm text-muted-foreground">Define conditions that must be met</span>
                </div>

                <ConditionBuilder
                    conditions={conditions}
                    onChange={setConditions}
                    schemaColumns={schemaColumns}
                    eventType={eventType === 'data_change' ? dataChangeEvent : null}
                />
            </div>

            {/* ── THEN ── */}
            <div className="space-y-4">
                <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs font-bold px-3 py-1">THEN</Badge>
                    <span className="text-sm text-muted-foreground">Define what happens when conditions are met</span>
                </div>

                <div className="space-y-4 pl-4 border-l-2 border-primary/20">
                    {/* Action Type */}
                    <div className="space-y-1.5">
                        <Label className="text-sm">Action Type</Label>
                        <Select value="execute_workflow" disabled>
                            <SelectTrigger className="max-w-xs">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="execute_workflow">Execute a Workflow</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Workflow Name */}
                    <div className="space-y-1.5">
                        <Label className="text-sm">Workflow Name</Label>
                        <Input
                            value={workflowName}
                            onChange={(e) => setWorkflowName(e.target.value)}
                            placeholder="e.g., Process Order Fulfillment"
                            className="max-w-md"
                        />
                    </div>

                    {/* Input Mappings */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <Label className="text-sm">Input Mapping</Label>
                            <Button variant="ghost" size="sm" onClick={addInputMapping} className="h-7 text-xs">
                                <Plus className="mr-1 h-3 w-3" /> Add Input
                            </Button>
                        </div>

                        {inputMappings.length > 0 && (
                            <div className="space-y-2">
                                {inputMappings.map((mapping, idx) => (
                                    <div key={idx} className="flex items-center gap-2">
                                        <Input
                                            value={mapping.inputName}
                                            onChange={(e) => updateInputMapping(idx, 'inputName', e.target.value)}
                                            placeholder="Input name"
                                            className="flex-1"
                                        />
                                        <Input
                                            value={mapping.value}
                                            onChange={(e) => updateInputMapping(idx, 'value', e.target.value)}
                                            placeholder="{{trigger.record.after.id}}"
                                            className="flex-1 font-mono text-sm"
                                        />
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 text-red-500 shrink-0"
                                            onClick={() => removeInputMapping(idx)}
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Available placeholders hint */}
                        {triggerPlaceholders.length > 0 && (
                            <div className="p-3 border rounded-md bg-muted/20">
                                <p className="text-xs text-muted-foreground mb-2">Available trigger data:</p>
                                <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                                    {triggerPlaceholders.map((p) => (
                                        <Badge
                                            key={p}
                                            variant="secondary"
                                            className="text-[10px] cursor-pointer hover:bg-primary/10 transition-colors font-mono"
                                            onClick={() => {
                                                // Copy to clipboard
                                                navigator.clipboard.writeText(p);
                                            }}
                                        >
                                            {p}
                                        </Badge>
                                    ))}
                                </div>
                                <p className="text-[10px] text-muted-foreground mt-1">Click to copy a placeholder.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TriggerEditor;
